import Phaser from 'phaser';
import type { AnimationDefinition } from '../../sprites/AnimationSpriteManager';
import { GAME_FONT_FAMILY } from '@shared/constants';

/**
 * Helper para agregar la fuente del juego y resolución alta a un estilo de texto.
 * La resolución alta hace que los textos se vean nítidos incluso con pixelArt: true.
 */
function withGameFont<T extends Phaser.Types.GameObjects.Text.TextStyle>(style?: T): T {
  return {
    fontFamily: GAME_FONT_FAMILY,
    resolution: 2, // Resolución alta para textos nítidos
    ...style,
  } as T;
}

type AnimPanelCallbacks = {
  onMove(x: number, y: number): void;
  onPlayAnimation(animationId: number): void;
};

type AnimPanelOptions = {
  initialX: number;
  initialY: number;
};

type AnimPanelElements = {
  bg: Phaser.GameObjects.Rectangle;
  title: Phaser.GameObjects.Text;
  list: Phaser.GameObjects.Text[];
  invokeBtn: Phaser.GameObjects.Text;
  closeButton: Phaser.GameObjects.Text;
  panelX: number;
  panelY: number;
  panelWidth: number;
  panelHeight: number;
};

export class AnimPanel {
  private readonly scene: Phaser.Scene;
  private readonly callbacks: AnimPanelCallbacks;
  private panel: AnimPanelElements | null = null;
  private isOpen = false;
  private isDragging = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;
  private animationsIndex: Record<string, AnimationDefinition> | null = null;
  private selectedIndex: number = -1;

  constructor(scene: Phaser.Scene, callbacks: AnimPanelCallbacks, options: AnimPanelOptions) {
    this.scene = scene;
    this.callbacks = callbacks;
    this.buildPanel(options.initialX, options.initialY);
    this.setVisible(false);
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
    this.setVisible(this.isOpen);
  }

  open(): void {
    this.isOpen = true;
    this.setVisible(true);
  }

  close(): void {
    this.isOpen = false;
    this.setVisible(false);
  }

  isOpenPanel(): boolean {
    return this.isOpen;
  }

  setAnimationsIndex(animationsIndex: Record<string, AnimationDefinition> | null): void {
    this.animationsIndex = animationsIndex;
    this.refreshList();
  }

  getAnimationsIndex(): Record<string, AnimationDefinition> | null {
    return this.animationsIndex;
  }

  handlePointerMove(pointer: Phaser.Input.Pointer): void {
    if (!this.isDragging || !this.panel) return;
    const newX = pointer.x - this.dragOffsetX;
    const newY = pointer.y - this.dragOffsetY;
    this.move(newX, newY);
    this.callbacks.onMove(newX, newY);
  }

  handlePointerUp(pointer: Phaser.Input.Pointer): void {
    if (this.isDragging) {
      this.isDragging = false;
      pointer.event.stopPropagation();
    }
  }

  isDraggingPanel(): boolean {
    return this.isDragging;
  }

  isPointerOver(pointer: Phaser.Input.Pointer): boolean {
    if (!this.panel || !this.isOpen) return false;
    const { panelX, panelY, panelWidth, panelHeight } = this.panel;
    const left = panelX - panelWidth / 2;
    const right = panelX + panelWidth / 2;
    const top = panelY - panelHeight / 2;
    const bottom = panelY + panelHeight / 2;
    return pointer.x >= left && pointer.x <= right && pointer.y >= top && pointer.y <= bottom;
  }

  private buildPanel(initialX: number, initialY: number): void {
    const panelWidth = 300;
    const panelHeight = 280;
    const panelX = initialX;
    const panelY = initialY;

    const bg = this.scene.add
      .rectangle(panelX, panelY, panelWidth, panelHeight, 0x111111, 0.9)
      .setOrigin(0.5)
      .setStrokeStyle(1, 0x666666)
      .setDepth(1100)
      .setScrollFactor(0)
      // Fondo interactivo: absorbe hover/click de objetos del mundo debajo
      // (Phaser topOnly) para que la ventana no deje pasar clicks ni cambie el cursor.
      .setInteractive()
      .setVisible(false);

    const baseX = panelX - panelWidth / 2;
    const baseY = panelY - panelHeight / 2;

    const title = this.scene.add
      .text(panelX, baseY + 16, 'Animaciones', withGameFont({
        fontSize: '16px',
        color: '#ffffff',
      }))
      .setOrigin(0.5, 0.5)
      .setDepth(1100)
      .setScrollFactor(0)
      .setVisible(false)
      .setInteractive({ useHandCursor: true });

    title.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.isDragging = true;
      this.dragOffsetX = pointer.x - bg.x;
      this.dragOffsetY = pointer.y - bg.y;
      pointer.event.stopPropagation();
    });

    const listStartY = baseY + 40;
    const rowHeight = 22;
    const list: Phaser.GameObjects.Text[] = [];

    for (let i = 0; i < 10; i += 1) {
      const y = listStartY + i * rowHeight;
      const txt = this.scene.add
        .text(baseX + 12, y, '', withGameFont({
          fontSize: '13px',
          color: '#ffffff',
        }))
        .setOrigin(0, 0)
        .setInteractive({ useHandCursor: true })
        .setDepth(1101)
        .setScrollFactor(0)
        .setVisible(false);

      const index = i;
      txt.on('pointerup', (pointer: Phaser.Input.Pointer) => {
        this.handleListClick(index);
        pointer.event.stopPropagation();
      });

      list.push(txt);
    }

    const invokeBtn = this.scene.add
      .text(panelX, panelY + panelHeight / 2 - 28, '[ Invocar ]', withGameFont({
        fontSize: '14px',
        color: '#ffffaa',
        backgroundColor: '#333333',
      }))
      .setOrigin(0.5, 0.5)
      .setPadding(8, 4, 8, 4)
      .setInteractive({ useHandCursor: true })
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false);

    invokeBtn.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.handleInvoke();
      pointer.event.stopPropagation();
    });

    const closeButton = this.scene.add
      .text(panelX + panelWidth / 2 - 16, baseY + 8, '✕', withGameFont({
        fontSize: '14px',
        color: '#ffffff',
        backgroundColor: '#555555',
      }))
      .setOrigin(1, 0)
      .setPadding(4, 2, 4, 2)
      .setInteractive({ useHandCursor: true })
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false);

    closeButton.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.close();
      pointer.event.stopPropagation();
    });

    this.panel = {
      bg,
      title,
      list,
      invokeBtn,
      closeButton,
      panelX,
      panelY,
      panelWidth,
      panelHeight,
    };
  }

  private handleListClick(index: number): void {
    if (!this.animationsIndex) return;
    const entries = Object.entries(this.animationsIndex);
    if (index >= entries.length) return;
    this.selectedIndex = index;
    this.refreshList();
  }

  private handleInvoke(): void {
    if (this.selectedIndex < 0 || !this.animationsIndex) return;
    const entries = Object.entries(this.animationsIndex);
    if (this.selectedIndex >= entries.length) return;
    const [key] = entries[this.selectedIndex];
    const animId = parseInt(key.replace('animation', ''), 10);
    if (!Number.isNaN(animId)) {
      this.callbacks.onPlayAnimation(animId);
    }
  }

  private refreshList(): void {
    if (!this.panel || !this.animationsIndex) return;

    const entries = Object.entries(this.animationsIndex);

    for (let i = 0; i < this.panel.list.length; i += 1) {
      const txt = this.panel.list[i];

      if (i < entries.length) {
        const [key, def] = entries[i];
        const id = parseInt(key.replace('animation', ''), 10);
        txt.setText(`${id}. ${def.name} (${def.type})`);
        txt.setData('animId', id);
        txt.setColor(i === this.selectedIndex ? '#ffff00' : '#ffffff');
      } else {
        txt.setText('');
        txt.setData('animId', null);
      }
    }
  }

  private setVisible(visible: boolean): void {
    if (!this.panel) return;
    const panel = this.panel;
    panel.bg.setVisible(visible).setActive(visible);
    panel.title.setVisible(visible).setActive(visible);
    panel.list.forEach((txt) => txt.setVisible(visible).setActive(visible));
    panel.invokeBtn.setVisible(visible).setActive(visible);
    panel.closeButton.setVisible(visible).setActive(visible);
    if (visible) {
      this.refreshList();
    }
  }

  private move(newX: number, newY: number): void {
    if (!this.panel) return;
    const panel = this.panel;
    panel.panelX = newX;
    panel.panelY = newY;

    const { panelWidth, panelHeight } = panel;
    const baseX = newX - panelWidth / 2;
    const baseY = newY - panelHeight / 2;
    const listStartY = baseY + 40;
    const rowHeight = 22;

    panel.bg.setPosition(newX, newY);
    panel.title.setPosition(newX, baseY + 16);

    panel.list.forEach((txt, i) => {
      txt.setPosition(baseX + 12, listStartY + i * rowHeight);
    });

    panel.invokeBtn.setPosition(newX, newY + panelHeight / 2 - 28);
    panel.closeButton.setPosition(newX + panelWidth / 2 - 16, baseY + 8);
  }
}

